import {
  getCookie,
  setCookie,
  unsetCookie,
} from '@wandb/weave/common/util/cookie';
import {isTruthy} from '@wandb/weave/common/util/types';

import {envIsPublicCloud, envIsServerDeployment} from '../config';
import {useViewer} from '../state/viewer/hooks';
import {type Viewer} from '../state/viewer/types';

const USE_ADMIN_PRIVILEGES_COOKIE_KEY = 'use_admin_privileges';
export const USE_VIEW_AS_COOKIE_KEY = 'impersonated_username';

const {host} = window.location;
const isLocalhost = host.includes('localhost');

/**
 * Notes on admin mode:
 * The viewer.admin flag indicates whether a valid Teleport request has granted temporary admin privileges so that the backend will accept requests made using admin mode (not whether the user is currently using them). The admin mode toggle in the UI is controlled by the `use_admin_privileges` session cookie, independent of the Teleport approval expiry. Thus:
 * - If viewer.admin is false → user is never in admin mode, backend won't allow requests
 * - If viewer.admin is true → cookie determines if admin mode is toggled on or not
 */

// Important - this will assume server org admins have admin powers enabled
// and be treated like w&b employees. Consider `useIsAdminModeActiveAndIsNotLocalEnv`
export function useAdminModeActive(): boolean {
  const viewer = useViewer();
  if (!viewer?.admin) {
    return false;
  }
  return viewerUsingAdminPrivileges();
}

export function useIsAdminModeActiveAndIsNotLocalEnv(): boolean {
  const viewer = useViewer();
  return viewerIsWbAdminUsingPrivileges(viewer);
}

export function viewerIsWbAdminUsingPrivileges(viewer: Viewer | undefined) {
  if (envIsServerDeployment) {
    return false; // Not a concept in server, so default to false
  }
  if (!viewer?.admin) {
    return false;
  }
  return getCookie(USE_ADMIN_PRIVILEGES_COOKIE_KEY) === 'true';
}

export function isInternalUser(viewer: Viewer | undefined) {
  const email = viewer?.email ?? '';
  const isWandbDomain = isWandbDomainEmail(email);
  const isCoreWeaveDomain = isCoreWeaveDomainEmail(email);

  return !!email && (isWandbDomain || isCoreWeaveDomain);
}

export function viewerIsWbAdmin(viewer: Viewer | undefined) {
  if (envIsServerDeployment) {
    return false;
  }
  const isInternal =
    viewer?.admin &&
    viewer?.email &&
    (isWandbDomainEmail(viewer?.email) ||
      isCoreWeaveDomainEmail(viewer?.email));

  if (isInternal) {
    return true;
  }
  return false;
}

export function viewerUsingAdminPrivileges(): boolean {
  if (!envIsPublicCloud) {
    return true;
  }
  return getCookie(USE_ADMIN_PRIVILEGES_COOKIE_KEY) === 'true';
}

export function viewingAs(): string {
  return getCookie(USE_VIEW_AS_COOKIE_KEY);
}

export function setViewerUsingAdminPrivileges(enable: boolean): void {
  if (enable) {
    setAdminCookieForSubdomain(USE_ADMIN_PRIVILEGES_COOKIE_KEY, 'true');
  } else {
    unsetCookie(USE_ADMIN_PRIVILEGES_COOKIE_KEY);
  }
}

export function setViewingAs(username: string): void {
  setCookie(USE_VIEW_AS_COOKIE_KEY, username);
}

export function unsetViewingAs(): void {
  unsetCookie(USE_VIEW_AS_COOKIE_KEY);
}

const wandbDomainEmailRegex = new RegExp('^.+?@wandb.com$');
export function isWandbDomainEmail(email: string): boolean {
  return wandbDomainEmailRegex.test(email);
}

const coreWeaveDomainEmailRegex = new RegExp('^.+?@coreweave.com$');
export function isCoreWeaveDomainEmail(email: string): boolean {
  return coreWeaveDomainEmailRegex.test(email);
}

// modified version of setCookie to avoid setting duplicate admin cookies
// in test environments (makes it harder to test admin mode)
function setAdminCookieForSubdomain(key: string, value: string): void {
  if (!key) {
    return;
  }

  // take original path if prod
  if (host.startsWith('wandb.ai')) {
    setCookie(key, value);
  }

  // otherwise set a subdomain cookie
  const keyValStr = `${key}=${value}`;
  const expiryStr = ``;

  let devCookieAttrs = `domain=${host}; SameSite=strict; path=/`;
  if (isLocalhost) {
    devCookieAttrs = 'path=/';
  }

  for (const attrsStr of [devCookieAttrs]) {
    document.cookie = [keyValStr, expiryStr, attrsStr]
      .filter(isTruthy)
      .join('; ');
  }
}
